import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useAuthStore } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";

// Eager: small, on the public path
import Landing from "@/routes/Landing";

// Lazy: gated routes — load only after auth
const Home = lazy(() => import("@/routes/Home"));
const About = lazy(() => import("@/routes/About"));
const Pricing = lazy(() => import("@/routes/Pricing"));
const Contact = lazy(() => import("@/routes/Contact"));
const Disclaimer = lazy(() => import("@/routes/Disclaimer"));
const TermsOfService = lazy(() => import("@/routes/TermsOfService"));

const Options = lazy(() => import("@/routes/tools/Options"));
const OptionChart = lazy(() => import("@/routes/tools/OptionChart"));
const Screener = lazy(() => import("@/routes/tools/Screener"));
const Grid = lazy(() => import("@/routes/tools/Grid"));
const Calculator = lazy(() => import("@/routes/tools/Calculator"));
const Watchlist = lazy(() => import("@/routes/tools/Watchlist"));
const Volatility = lazy(() => import("@/routes/tools/Volatility"));
const OptionHistory = lazy(() => import("@/routes/tools/OptionHistory"));

const Members = lazy(() => import("@/routes/members/Members"));
const Profile = lazy(() => import("@/routes/members/Profile"));
const Forums = lazy(() => import("@/routes/members/Forums"));
const ForumThread = lazy(() => import("@/routes/members/ForumThread"));

const SignUp = lazy(() => import("@/routes/auth/SignUp"));
const Register = lazy(() => import("@/routes/auth/Register"));
const Join = lazy(() => import("@/routes/auth/Join"));
const Activate = lazy(() => import("@/routes/auth/Activate"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Guard: any /app/* route redirects to / when not unlocked. */
function RequireAuth() {
  const unlocked = useAuthStore((s) => s.unlocked);
  return unlocked ? <Outlet /> : <Navigate to="/" replace />;
}

function PageFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="size-6 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public landing */}
          <Route path="/" element={<Landing />} />

          {/* Authenticated app */}
          <Route element={<RequireAuth />}>
            <Route
              path="/app"
              element={
                <Suspense fallback={<PageFallback />}>
                  <AppShell />
                </Suspense>
              }
            >
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="contact" element={<Contact />} />
              <Route path="disclaimer" element={<Disclaimer />} />
              <Route path="terms-of-service" element={<TermsOfService />} />

              <Route path="options" element={<Options />} />
              <Route path="option-chart" element={<OptionChart />} />
              <Route path="screener" element={<Screener />} />
              <Route path="grid" element={<Grid />} />
              <Route path="calculator" element={<Calculator />} />
              <Route path="watchlist" element={<Watchlist />} />
              <Route path="volatility" element={<Volatility />} />
              <Route path="option-history" element={<OptionHistory />} />

              <Route path="members" element={<Members />} />
              <Route path="profile/:username" element={<Profile />} />
              <Route path="forums" element={<Forums />} />
              <Route path="forums/:slug" element={<ForumThread />} />

              <Route path="sign-up" element={<SignUp />} />
              <Route path="register" element={<Register />} />
              <Route path="join" element={<Join />} />
              <Route path="activate" element={<Activate />} />
            </Route>
          </Route>

          {/* Catch-all → home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
